import { useState, useRef, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, UserRound, Users, Zap } from "lucide-react";
import { cn } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { apiFetchWithRetry } from "@/lib/apiFetch";
import { LocationAutocompleteInput, type LocationValue } from "@/components/location/LocationAutocompleteInput";

interface Topic {
  id: string;
  name: string;
}

interface Subject {
  id: string;
  name: string;
  topics: Topic[];
}

interface SearchBarProps {
  initialQuery?: string;
  initialLocation?: LocationValue | null;
  onSearch?: (query: string, topicId?: string, location?: LocationValue | null) => void;
  compact?: boolean;
}

const TYPE_OPTIONS: {
  value: "one-to-one" | "group" | "crash-course";
  label: string;
  Icon: React.ElementType;
  activeCls: string;
}[] = [
  { value: "one-to-one",   label: "One-to-One",    Icon: UserRound, activeCls: "bg-[hsl(237,52%,31%)] text-white" },
  { value: "group",        label: "Group Classes",  Icon: Users,     activeCls: "bg-violet-600 text-white" },
  { value: "crash-course", label: "Crash Courses",  Icon: Zap,       activeCls: "bg-amber-500 text-white" },
];

export function SearchBar({ initialQuery = "", initialLocation, onSearch, compact = false }: SearchBarProps) {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [offeringType, setOfferingType] = useState<"one-to-one" | "group" | "crash-course">("one-to-one");
  const [location, setLocation] = useState<LocationValue | null>(initialLocation || null);
  const [searchFocused, setSearchFocused] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const barRef = useRef<HTMLDivElement>(null);

  // Load catalog subjects + topics for autocomplete
  const { data: subjects } = useQuery<Subject[]>({
    queryKey: ['catalog-subjects'],
    queryFn: async () => {
      const res = await apiFetchWithRetry('/api/v1/catalog/subjects');
      if (!res.ok) return [];
      return res.json();
    },
    staleTime: 5 * 60 * 1000,
  });

  // Flatten subjects into topic list with parent subjectId attached
  const allTopics = useMemo(() => {
    if (!subjects) return [];
    return subjects.flatMap(s => s.topics.map(t => ({ ...t, subjectId: s.id, subjectName: s.name })));
  }, [subjects]);

  // Suggestions: topics matching the query
  const suggestions = useMemo(() => {
    if (!searchQuery || searchQuery.length < 2) return [];
    const q = searchQuery.toLowerCase();
    return allTopics.filter(t =>
      t.name.toLowerCase().includes(q) || t.subjectName.toLowerCase().includes(q)
    ).slice(0, 8);
  }, [searchQuery, allTopics]);

  const [showSubjectError, setShowSubjectError] = useState(false);

  const handleSearch = (query?: string, topicId?: string, subjectId?: string) => {
    const q = query ?? searchQuery;
    if (!q.trim()) {
      setShowSubjectError(true);
      return;
    }
    setShowSubjectError(false);
    setShowDropdown(false);

    if (onSearch) {
      onSearch(q, topicId, location);
      return;
    }

    const params = new URLSearchParams();
    params.set("q", q);
    // Pass both subjectId (for server-side subject scoping) and topics (for topic filter chip)
    if (subjectId) params.set("subjectId", subjectId);
    if (topicId) params.append("topics", topicId);
    if (location) {
      params.set("lat", String(location.lat));
      params.set("lng", String(location.lng));
      params.set("locationName", location.displayName);
      params.set("radiusKm", "25");
    }
    navigate(`/search/${offeringType}?${params.toString()}`);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      if (selectedIndex >= 0 && suggestions[selectedIndex]) {
        const topic = suggestions[selectedIndex];
        setSearchQuery(topic.name);
        handleSearch(topic.name, topic.id, topic.subjectId);
      } else {
        handleSearch();
      }
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex(i => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex(i => Math.max(i - 1, -1));
    } else if (e.key === "Escape") {
      setShowDropdown(false);
    }
  };

  const handleSelectTopic = (topic: typeof allTopics[0]) => {
    setSearchQuery(topic.name);
    handleSearch(topic.name, topic.id, topic.subjectId);
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (barRef.current && !barRef.current.contains(e.target as Node)) setShowDropdown(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="relative" ref={barRef}>

      {/* ── Offering-type selector ── */}
      {!compact && (
        <div className="flex items-center gap-1 mb-3 p-1 bg-white/80 border border-white/60 rounded-full w-fit mx-auto shadow-sm backdrop-blur-sm">
          {TYPE_OPTIONS.map(({ value, label, Icon, activeCls }) => (
            <button
              key={value}
              type="button"
              onClick={() => setOfferingType(value)}
              aria-pressed={offeringType === value}
              className={cn(
                "inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold rounded-full cursor-pointer transition-all duration-150",
                offeringType === value
                  ? `${activeCls} shadow-sm`
                  : "text-slate-500 hover:text-slate-800 hover:bg-slate-100"
              )}
            >
              <Icon className="w-3 h-3" />
              {label}
            </button>
          ))}
        </div>
      )}

      {/* ── Mobile layout ── */}
      <div className="flex flex-col sm:hidden gap-2">
        <div className={cn(
          "flex items-center gap-3 px-4 bg-white border rounded-2xl shadow-lg transition-all duration-300",
          showSubjectError ? "border-red-400" : searchFocused ? "border-[hsl(237,52%,31%)]/40" : "border-border"
        )}>
          <Search className={cn("w-4 h-4 flex-shrink-0", searchFocused ? "text-[hsl(237,52%,31%)]" : "text-muted-foreground")} />
          <input
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setShowDropdown(true); setSelectedIndex(-1); if (e.target.value.trim()) setShowSubjectError(false); }}
            onKeyDown={handleKeyDown}
            onFocus={() => { setSearchFocused(true); if (searchQuery.length >= 2) setShowDropdown(true); }}
            onBlur={() => setSearchFocused(false)}
            placeholder="Subject or topic..."
            className="flex-1 py-4 text-sm bg-transparent text-foreground placeholder:text-muted-foreground outline-none"
          />
          {searchQuery && (
            <button type="button" onClick={() => { setSearchQuery(""); setShowDropdown(false); setSelectedIndex(-1); }} className="p-1 text-muted-foreground hover:text-foreground rounded-full" aria-label="Clear search">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        <div className="bg-white border border-border rounded-2xl shadow-sm px-1">
          <LocationAutocompleteInput
            value={location}
            onChange={setLocation}
            placeholder="Where?"
            className="border-0 shadow-none bg-transparent [&>div:first-child]:border-0 [&>div:first-child]:shadow-none [&>div:first-child]:bg-transparent"
          />
        </div>

        <button
          type="button"
          onClick={() => handleSearch()}
          className="w-full py-3.5 rounded-2xl bg-[hsl(237,52%,31%)] hover:bg-[hsl(237,52%,25%)] text-white text-sm font-semibold transition-all shadow-md"
        >
          Search
        </button>
      </div>

      {/* ── Desktop layout: pill ── */}
      <div
        className={cn(
          "hidden sm:flex items-center bg-white border rounded-full shadow-lg transition-all duration-300 overflow-visible",
          showSubjectError
            ? "border-red-400 shadow-[0_0_0_3px_rgba(248,113,113,0.2)]"
            : searchFocused
            ? "border-[hsl(237,52%,31%)]/40 shadow-[0_0_0_3px_hsl(237,52%,31%,0.10)]"
            : "border-border hover:shadow-xl hover:border-border/80"
        )}
      >
        {/* Subject search */}
        <div className="flex-1 flex items-center gap-3 px-5">
          <Search className={cn(
            "w-5 h-5 flex-shrink-0 transition-colors",
            searchFocused ? "text-[hsl(237,52%,31%)]" : "text-muted-foreground"
          )} />
          <input
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setShowDropdown(true); setSelectedIndex(-1); if (e.target.value.trim()) setShowSubjectError(false); }}
            onKeyDown={handleKeyDown}
            onFocus={() => { setSearchFocused(true); if (searchQuery.length >= 2) setShowDropdown(true); }}
            onBlur={() => setSearchFocused(false)}
            placeholder="What would you like to learn?"
            className={cn(
              "w-full bg-transparent text-foreground placeholder:text-muted-foreground outline-none",
              compact ? "py-2.5 text-sm" : "py-5 text-sm sm:text-base"
            )}
          />
        </div>
        {searchQuery && (
          <button
            type="button"
            onClick={() => { setSearchQuery(""); setShowDropdown(false); setSelectedIndex(-1); }}
            className="p-1.5 mr-1 text-muted-foreground hover:text-foreground transition-colors rounded-full hover:bg-accent"
            aria-label="Clear search"
          >
            <X className="w-4 h-4" />
          </button>
        )}

        {/* Divider */}
        <div className="w-px h-7 bg-border/60 flex-shrink-0" />

        {/* Location search */}
        <div className={cn("flex-1 px-3", compact ? "py-1" : "py-2")}>
          <LocationAutocompleteInput
            value={location}
            onChange={setLocation}
            placeholder="Where?"
            className="border-0 shadow-none bg-transparent [&>div:first-child]:border-0 [&>div:first-child]:shadow-none [&>div:first-child]:bg-transparent [&>div:first-child]:px-0"
          />
        </div>

        <div className="pr-2">
          <button
            type="button"
            onClick={() => handleSearch()}
            className={cn(
              "rounded-full bg-[hsl(237,52%,31%)] hover:bg-[hsl(237,52%,25%)] text-white font-semibold transition-all duration-200 active:brightness-95 shadow-md hover:shadow-lg",
              compact ? "px-5 py-2 text-xs" : "px-7 py-3.5 text-sm"
            )}
          >
            Search
          </button>
        </div>
      </div>

      {/* Subject required error */}
      {showSubjectError && (
        <p className="text-xs text-red-500 mt-2 ml-5 text-left">Please enter a subject or topic to search.</p>
      )}

      {/* ── Autocomplete dropdown ── */}
      {showDropdown && suggestions.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-2 z-50 overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-[0_8px_32px_rgba(0,0,0,0.12),0_2px_8px_rgba(0,0,0,0.06)]">
          {suggestions.map((topic, i) => (
            <button
              key={topic.id}
              onMouseDown={() => handleSelectTopic(topic)}
              className={cn(
                "w-full text-left px-5 py-3 text-sm transition-colors duration-100 flex items-center gap-3 group",
                i === selectedIndex
                  ? "bg-[hsl(237,52%,31%)]/8 text-[hsl(237,52%,31%)]"
                  : "text-slate-700 hover:bg-[hsl(237,52%,31%)]/5 hover:text-[hsl(237,52%,31%)]",
                i > 0 && "border-t border-slate-50"
              )}
            >
              <Search className={cn(
                "w-3.5 h-3.5 shrink-0 transition-colors",
                i === selectedIndex ? "text-[hsl(237,52%,31%)]" : "text-slate-400 group-hover:text-[hsl(237,52%,31%)]"
              )} />
              <div className="min-w-0">
                <span className="font-medium">{topic.name}</span>
                <span className="text-xs text-slate-400 ml-2">{topic.subjectName}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
